import { useState } from 'react'
import { useAuthenticate } from 'core/components/Authenticate'
import { useGetLiveRoomGetLiveRoomListQuery } from 'core/query/yapi/hk-web/LiveRoom/query'
import { useGetImQueryUserForbidStatusQuery } from 'core/query/yapi/hk-web/IMTencent/query'

/**
 * 直播列表 Hook 参数
 */
interface UseLiveListOptions {
  /** 是否启用查询 */
  enabled?: boolean
}

/**
 * 直播列表 Hook
 * @param options - Hook 配置选项
 * @param options.enabled - 是否启用查询，默认为 true
 * @returns {Object} 返回直播列表相关数据
 * - liveIngList: 正在直播的列表
 * - liveAwaitList: 待直播的列表
 * - isLoading: 加载状态
 * - refetchLiving: 重新获取直播中数据的函数
 */
export const useLiveList = ({ enabled = true }: UseLiveListOptions = {}) => {
  // 获取待直播列表数据 (status: 0)
  const { data: toBeLivingdata, isLoading: isToBeLivingLoading } = useGetLiveRoomGetLiveRoomListQuery(
    { status: 0 },
    {
      staleTime: 0, // 不缓存数据
      enabled, // 控制查询是否执行
    }
  )

  // 获取正在直播列表数据 (status: 1)
  const {
    data: livingdata,
    isLoading: isLivingLoading,
    refetch: refetchLiving,
  } = useGetLiveRoomGetLiveRoomListQuery(
    { status: 1 },
    {
      staleTime: 0,
      enabled,
    }
  )

  // 合并加载状态
  const isLoading = isLivingLoading || isToBeLivingLoading

  // 合并两个列表数据
  const data = [...(toBeLivingdata?.data?.data || []), ...(livingdata?.data?.data || [])]

  // 当正在加载时显示6个占位项，否则过滤出正在直播的列表
  const liveIngList = isLoading ? [{}, {}, {}, {}, {}, {}] : data?.filter((i) => i.status === 1)
  // 当正在加载时显示6个占位项，否则过滤出待直播的列表
  const liveAwaitList = isLoading ? [{}, {}, {}, {}, {}, {}] : data?.filter((i) => i.status === 0)

  return {
    liveIngList,
    liveAwaitList,
    isLoading,
    refetchLiving,
  }
}

/**
 * 用户禁用状态 Hook
 * @returns {Object} 返回禁用状态相关数据和方法
 * - showTips: 是否显示提示
 * - setShowTips: 设置提示显示状态的函数
 * - handleBanModal: 处理禁用模态框的函数
 */
export const useBanStatus = () => {
  const [showTips, setShowTips] = useState(false)
  const [roomId, setRoomId] = useState(0)
  const auth = useAuthenticate()

  // 获取用户禁用状态
  const { data, refetch } = useGetImQueryUserForbidStatusQuery(
    {
      roomId,
      userId: auth?.loginData?.fid,
    },
    {
      enabled: false, // 默认不启用查询
      staleTime: 0, // 不缓存数据
    }
  )

  /**
   * 处理禁用模态框
   * @param id - 房间ID
   * @param router - 路由对象
   */
  const handleBanModal = async (id: number = 0, router: any, fromList?: string) => {
    // 未登录时直接跳转
    if (!auth?.loginData) {
      return router.push(`/live/room?id=${id}&fromList=${fromList || '0'}`)
    }
    setRoomId(id)

    // 等待获取到数据
    // await new Promise((resolve) => setTimeout(resolve, 50))
    // await refetch()
    // if (data?.data?.data?.forbidStatus) {
    //   setShowTips(true)
    // } else {
    //   router.push(`/live/room?id=${id}`)
    // }

    // 直接跳转到直播间
    router.push(`/live/room?id=${id}&fromList=${fromList || '0'}`)
  }

  return {
    showTips,
    setShowTips,
    handleBanModal,
  }
}
